import {
  IAgentWorkflow,
  IOutboundPipelineStats,
  IOutboundProspect,
  IWorkflowRun
} from "~/types/agent-os"
import { ITokenInfo } from "~/types/global"
import { axiosInstance } from "~/utils/axios"

export async function getAgentWorkflows(
  tokenInfo: ITokenInfo,
  useOsLitePrefix = true
): Promise<{ data: IAgentWorkflow[] }> {
  const prefix = useOsLitePrefix && tokenInfo.is_os_lite_user ? "os_lite/" : ""
  const url = `${prefix}tenants/${tokenInfo.tenant_id}/users/${tokenInfo.user_id}/agent-workflows`

  return await axiosInstance({
    method: "GET",
    url,
    headers: {
      Authorization: `Bearer ${tokenInfo?.token}`
    }
  })
}

export async function getAgentWorkflow(
  tokenInfo: ITokenInfo,
  workflowId: string
): Promise<{ data: IAgentWorkflow | null }> {
  const prefix = tokenInfo.is_os_lite_user ? "os_lite/" : ""
  const url = `${prefix}tenants/${tokenInfo.tenant_id}/users/${tokenInfo.user_id}/agent-workflows/${workflowId}`

  return await axiosInstance({
    method: "GET",
    url,
    headers: {
      Authorization: `Bearer ${tokenInfo?.token}`
    }
  })
}

export async function createAgentWorkflow(
  tokenInfo: ITokenInfo,
  payload: Partial<IAgentWorkflow>,
  useOsLitePrefix = true
): Promise<{ data: IAgentWorkflow }> {
  const prefix = useOsLitePrefix && tokenInfo.is_os_lite_user ? "os_lite/" : ""
  const url = `${prefix}tenants/${tokenInfo.tenant_id}/users/${tokenInfo.user_id}/agent-workflows`

  return await axiosInstance({
    method: "POST",
    url,
    headers: {
      Authorization: `Bearer ${tokenInfo?.token}`
    },
    data: payload
  })
}

export async function updateAgentWorkflow(
  tokenInfo: ITokenInfo,
  workflowId: string,
  payload: Partial<IAgentWorkflow>,
  useOsLitePrefix = true
): Promise<{ data: IAgentWorkflow }> {
  const prefix = useOsLitePrefix && tokenInfo.is_os_lite_user ? "os_lite/" : ""
  const url = `${prefix}tenants/${tokenInfo.tenant_id}/users/${tokenInfo.user_id}/agent-workflows/${workflowId}`

  return await axiosInstance({
    method: "PUT",
    url,
    headers: {
      Authorization: `Bearer ${tokenInfo?.token}`
    },
    data: payload
  })
}

export async function deleteAgentWorkflow(
  tokenInfo: ITokenInfo,
  workflowId: string
): Promise<{ data: { success: boolean } }> {
  const prefix = tokenInfo.is_os_lite_user ? "os_lite/" : ""
  const url = `${prefix}tenants/${tokenInfo.tenant_id}/users/${tokenInfo.user_id}/agent-workflows/${workflowId}`

  return await axiosInstance({
    method: "DELETE",
    url,
    headers: {
      Authorization: `Bearer ${tokenInfo?.token}`
    }
  })
}

export async function toggleAgentWorkflow(
  tokenInfo: ITokenInfo,
  workflowId: string,
  isEnabled: boolean
): Promise<{ data: IAgentWorkflow }> {
  const prefix = tokenInfo.is_os_lite_user ? "os_lite/" : ""
  const url = `${prefix}tenants/${tokenInfo.tenant_id}/users/${tokenInfo.user_id}/agent-workflows/${workflowId}/toggle`

  return await axiosInstance({
    method: "PATCH",
    url,
    headers: {
      Authorization: `Bearer ${tokenInfo?.token}`
    },
    data: { is_enabled: isEnabled }
  })
}

export async function triggerAgentWorkflow(
  tokenInfo: ITokenInfo,
  workflowId: string,
  data?: Record<string, any>,
  useOsLitePrefix = true
): Promise<{ data: IWorkflowRun }> {
  const prefix = useOsLitePrefix && tokenInfo.is_os_lite_user ? "os_lite/" : ""
  const url = `${prefix}tenants/${tokenInfo.tenant_id}/users/${tokenInfo.user_id}/agent-workflows/${workflowId}/run`

  return await axiosInstance({
    method: "POST",
    url,
    headers: {
      Authorization: `Bearer ${tokenInfo?.token}`
    },
    data: data || {}
  })
}

export async function testRunAgentWorkflow(
  tokenInfo: ITokenInfo,
  workflowId: string
): Promise<{ data: IWorkflowRun }> {
  const prefix = tokenInfo.is_os_lite_user ? "os_lite/" : ""
  const url = `${prefix}tenants/${tokenInfo.tenant_id}/users/${tokenInfo.user_id}/agent-workflows/${workflowId}/test-run`

  return await axiosInstance({
    method: "POST",
    url,
    headers: {
      Authorization: `Bearer ${tokenInfo?.token}`
    }
  })
}

export async function getAgentWorkflowRuns(
  tokenInfo: ITokenInfo,
  workflowId: string,
  limit: number = 10,
  offset: number = 0,
  useOsLitePrefix = true
): Promise<{ data: IWorkflowRun[] }> {
  const prefix = useOsLitePrefix && tokenInfo.is_os_lite_user ? "os_lite/" : ""
  const url = `${prefix}tenants/${tokenInfo.tenant_id}/users/${tokenInfo.user_id}/agent-workflows/${workflowId}/runs`

  return await axiosInstance({
    method: "GET",
    url,
    headers: {
      Authorization: `Bearer ${tokenInfo?.token}`
    },
    params: { limit, offset }
  })
}

export async function startOutboundRun(
  tokenInfo: ITokenInfo,
  workflowId: string,
  config?: Record<string, any>,
  selectedAccountIds?: string[]
): Promise<any> {
  const url = `tenants/${tokenInfo.tenant_id}/users/${tokenInfo.user_id}/agent-workflows/${workflowId}/outbound-run`
  return await axiosInstance({
    method: "POST",
    url,
    headers: { Authorization: `Bearer ${tokenInfo.token}` },
    // RC1: when the user narrowed the account selection, send the ids so the
    // backend scopes stakeholder discovery to exactly those accounts (incl.
    // ones outside the run's default top-20). Omitted/empty ⇒ top-20 default.
    data: {
      config: config || {},
      ...(selectedAccountIds && selectedAccountIds.length > 0
        ? { selected_account_ids: selectedAccountIds }
        : {})
    }
  })
}

export async function getOutboundRunStatus(
  tokenInfo: ITokenInfo,
  workflowId: string,
  runId: string
): Promise<any> {
  const url = `tenants/${tokenInfo.tenant_id}/users/${tokenInfo.user_id}/agent-workflows/${workflowId}/runs/${runId}/status`
  return await axiosInstance({
    method: "GET",
    url,
    headers: { Authorization: `Bearer ${tokenInfo.token}` }
  })
}

export async function getLatestOutboundRun(
  tokenInfo: ITokenInfo,
  workflowId: string
): Promise<any> {
  const url = `tenants/${tokenInfo.tenant_id}/users/${tokenInfo.user_id}/agent-workflows/${workflowId}/latest-run`
  return await axiosInstance({
    method: "GET",
    url,
    headers: { Authorization: `Bearer ${tokenInfo.token}` }
  })
}

export async function loadMoreOutboundStakeholders(
  tokenInfo: ITokenInfo,
  workflowId: string,
  runId: string,
  accountOffset: number,
  accountLimit = 25
): Promise<any> {
  const url = `tenants/${tokenInfo.tenant_id}/users/${tokenInfo.user_id}/agent-workflows/${workflowId}/runs/${runId}/load-more-stakeholders`
  return await axiosInstance({
    method: "POST",
    url,
    headers: { Authorization: `Bearer ${tokenInfo.token}` },
    data: { account_offset: accountOffset, account_limit: accountLimit }
  })
}

export async function getOutboundStakeholders(
  tokenInfo: ITokenInfo,
  workflowId: string,
  runId?: string
): Promise<any> {
  const url = `tenants/${tokenInfo.tenant_id}/users/${tokenInfo.user_id}/agent-workflows/${workflowId}/stakeholders`
  return await axiosInstance({
    method: "GET",
    url,
    headers: { Authorization: `Bearer ${tokenInfo.token}` },
    params: runId ? { run_id: runId } : undefined
  })
}

export async function createOutboundStakeholder(
  tokenInfo: ITokenInfo,
  workflowId: string,
  payload: Record<string, any>
): Promise<any> {
  const url = `tenants/${tokenInfo.tenant_id}/users/${tokenInfo.user_id}/agent-workflows/${workflowId}/stakeholders`
  return await axiosInstance({
    method: "POST",
    url,
    headers: { Authorization: `Bearer ${tokenInfo.token}` },
    data: payload
  })
}

// Bulk CSV upload. The backend fills blank name/title/company from each row's
// LinkedIn URL (same as the own-list run) and ignores rows already on the run.
export async function bulkCreateOutboundStakeholders(
  tokenInfo: ITokenInfo,
  workflowId: string,
  rows: Array<Record<string, any>>
): Promise<any> {
  const url = `tenants/${tokenInfo.tenant_id}/users/${tokenInfo.user_id}/agent-workflows/${workflowId}/stakeholders/bulk`
  return await axiosInstance({
    method: "POST",
    url,
    headers: { Authorization: `Bearer ${tokenInfo.token}` },
    data: { rows }
  })
}

export async function deleteOutboundStakeholder(
  tokenInfo: ITokenInfo,
  workflowId: string,
  stakeholderId: string
): Promise<any> {
  const url = `tenants/${tokenInfo.tenant_id}/users/${tokenInfo.user_id}/agent-workflows/${workflowId}/stakeholders/${stakeholderId}`
  return await axiosInstance({
    method: "DELETE",
    url,
    headers: { Authorization: `Bearer ${tokenInfo.token}` }
  })
}

export async function patchOutboundStakeholder(
  tokenInfo: ITokenInfo,
  workflowId: string,
  stakeholderId: string,
  patch: Record<string, any>
): Promise<any> {
  const url = `tenants/${tokenInfo.tenant_id}/users/${tokenInfo.user_id}/agent-workflows/${workflowId}/stakeholders/${stakeholderId}`
  return await axiosInstance({
    method: "PATCH",
    url,
    headers: { Authorization: `Bearer ${tokenInfo.token}` },
    data: patch
  })
}

// Queues the initial sequence email. The backend only accepts stakeholders
// whose engagement_status is "pending" or "scheduled" and that already have a
// stored draft — anything else comes back in `skipped` with a reason.
export async function scheduleOutboundInitialSend(
  tokenInfo: ITokenInfo,
  stakeholders: { stakeholder_id: string; subject: string; body_html: string }[]
): Promise<any> {
  const url = `tenants/${tokenInfo.tenant_id}/users/${tokenInfo.user_id}/agent-os/outbound/sequence-send/schedule-initial`
  return await axiosInstance({
    method: "POST",
    url,
    headers: { Authorization: `Bearer ${tokenInfo.token}` },
    data: { stakeholders }
  })
}

export async function getOutboundReplies(
  tokenInfo: ITokenInfo,
  workflowId: string,
  limit: number = 200,
  offset: number = 0
): Promise<any> {
  const url = `tenants/${tokenInfo.tenant_id}/users/${tokenInfo.user_id}/agent-workflows/${workflowId}/replies`
  return await axiosInstance({
    method: "GET",
    url,
    headers: { Authorization: `Bearer ${tokenInfo.token}` },
    params: { limit, offset }
  })
}

// Campaign Analytics (Part F) — per-run funnel/key-metrics/per-step
// breakdown. runId omitted lets the backend resolve "latest run" so the view
// naturally follows a re-launched campaign instead of pinning to a stale run.
export async function getOutboundAnalytics(
  tokenInfo: ITokenInfo,
  workflowId: string,
  runId?: string,
  dateRange?: { startDate?: string; endDate?: string }
): Promise<any> {
  const url = `tenants/${tokenInfo.tenant_id}/users/${tokenInfo.user_id}/agent-workflows/${workflowId}/analytics`
  return await axiosInstance({
    method: "POST",
    url,
    headers: { Authorization: `Bearer ${tokenInfo.token}` },
    data: {
      ...(runId ? { run_id: runId } : {}),
      ...(dateRange?.startDate ? { start_date: dateRange.startDate } : {}),
      ...(dateRange?.endDate ? { end_date: dateRange.endDate } : {})
    }
  })
}

// The people behind one number on the Analytics page. Same POST-with-body
// convention as getOutboundAnalytics above, and the same run/date scoping, so
// the panel always answers for the window the number came from.
export async function getOutboundAnalyticsPeople(
  tokenInfo: ITokenInfo,
  workflowId: string,
  body: {
    metric: string
    scope: string
    stepKey?: string | null
    runId?: string
    dateRange?: { startDate?: string; endDate?: string }
  }
): Promise<any> {
  const url = `tenants/${tokenInfo.tenant_id}/users/${tokenInfo.user_id}/agent-workflows/${workflowId}/analytics/people`
  return await axiosInstance({
    method: "POST",
    url,
    headers: { Authorization: `Bearer ${tokenInfo.token}` },
    data: {
      metric: body.metric,
      scope: body.scope,
      ...(body.stepKey ? { step_key: body.stepKey } : {}),
      ...(body.runId ? { run_id: body.runId } : {}),
      ...(body.dateRange?.startDate
        ? { start_date: body.dateRange.startDate }
        : {}),
      ...(body.dateRange?.endDate ? { end_date: body.dateRange.endDate } : {})
    }
  })
}

// Outbound emails sent for a workflow (initial + follow-ups + auto-replies),
// oldest-first — for the monitoring timeline (ENG-2355).
export async function getOutboundSentEmails(
  tokenInfo: ITokenInfo,
  workflowId: string
): Promise<any> {
  const url = `tenants/${tokenInfo.tenant_id}/users/${tokenInfo.user_id}/agent-workflows/${workflowId}/sent-emails`
  return await axiosInstance({
    method: "GET",
    url,
    headers: { Authorization: `Bearer ${tokenInfo.token}` }
  })
}

export async function getOutboundTasks(
  tokenInfo: ITokenInfo,
  workflowId?: string
): Promise<any> {
  const url = `tenants/${tokenInfo.tenant_id}/users/${tokenInfo.user_id}/agent-os/tasks`
  return await axiosInstance({
    method: "GET",
    url,
    headers: { Authorization: `Bearer ${tokenInfo.token}` },
    params: workflowId ? { workflow_id: workflowId } : undefined
  })
}

export async function createOutboundTask(
  tokenInfo: ITokenInfo,
  payload: Record<string, any>
): Promise<any> {
  const url = `tenants/${tokenInfo.tenant_id}/users/${tokenInfo.user_id}/agent-os/tasks`
  return await axiosInstance({
    method: "POST",
    url,
    headers: { Authorization: `Bearer ${tokenInfo.token}` },
    data: payload
  })
}

export async function patchOutboundTask(
  tokenInfo: ITokenInfo,
  taskId: string,
  patch: Record<string, any>
): Promise<any> {
  const url = `tenants/${tokenInfo.tenant_id}/users/${tokenInfo.user_id}/agent-os/tasks/${taskId}`
  return await axiosInstance({
    method: "PATCH",
    url,
    headers: { Authorization: `Bearer ${tokenInfo.token}` },
    data: patch
  })
}

export async function deleteOutboundTask(
  tokenInfo: ITokenInfo,
  taskId: string
): Promise<any> {
  const url = `tenants/${tokenInfo.tenant_id}/users/${tokenInfo.user_id}/agent-os/tasks/${taskId}`
  return await axiosInstance({
    method: "DELETE",
    url,
    headers: { Authorization: `Bearer ${tokenInfo.token}` }
  })
}

export async function getEmailConnectionStatus(
  tokenInfo: ITokenInfo
): Promise<any> {
  const url = `tenants/${tokenInfo.tenant_id}/users/${tokenInfo.user_id}/agent-os/oauth/connection-status`
  return await axiosInstance({
    method: "GET",
    url,
    headers: { Authorization: `Bearer ${tokenInfo.token}` }
  })
}

// Deletes the stored OAuth token for this provider. Sending, reply monitoring
// and calendar access all stop until the user reconnects.
export async function disconnectOAuthProvider(
  tokenInfo: ITokenInfo,
  provider: "outlook" | "gmail"
): Promise<any> {
  const url = `tenants/${tokenInfo.tenant_id}/users/${tokenInfo.user_id}/oauth/disconnect`
  return await axiosInstance({
    method: "POST",
    url,
    headers: { Authorization: `Bearer ${tokenInfo.token}` },
    data: { provider }
  })
}

export async function getOutboundProspects(
  tokenInfo: ITokenInfo
): Promise<{ data: IOutboundProspect[] }> {
  const prefix = tokenInfo.is_os_lite_user ? "os_lite/" : ""
  const url = `${prefix}tenants/${tokenInfo.tenant_id}/users/${tokenInfo.user_id}/agent-workflows/outbound/prospects`

  return await axiosInstance({
    method: "GET",
    url,
    headers: {
      Authorization: `Bearer ${tokenInfo?.token}`
    }
  })
}

export async function getOutboundPipelineStats(
  tokenInfo: ITokenInfo
): Promise<{ data: IOutboundPipelineStats }> {
  const prefix = tokenInfo.is_os_lite_user ? "os_lite/" : ""
  const url = `${prefix}tenants/${tokenInfo.tenant_id}/users/${tokenInfo.user_id}/agent-workflows/outbound/stats`

  return await axiosInstance({
    method: "GET",
    url,
    headers: {
      Authorization: `Bearer ${tokenInfo?.token}`
    }
  })
}

// Per-user email signature, appended at send time; the createResponse envelope puts the payload at res.data.data.
type EmailSignatureResponse = {
  data: { data: { email_signature: string } }
}

export async function getEmailSignature(
  tokenInfo: ITokenInfo
): Promise<EmailSignatureResponse> {
  const url = `tenants/${tokenInfo.tenant_id}/users/${tokenInfo.user_id}/agent-os/email-signature`
  return await axiosInstance({
    method: "GET",
    url,
    headers: { Authorization: `Bearer ${tokenInfo?.token}` }
  })
}

export async function updateEmailSignature(
  tokenInfo: ITokenInfo,
  email_signature: string
): Promise<EmailSignatureResponse> {
  const url = `tenants/${tokenInfo.tenant_id}/users/${tokenInfo.user_id}/agent-os/email-signature`
  return await axiosInstance({
    method: "PUT",
    url,
    headers: { Authorization: `Bearer ${tokenInfo?.token}` },
    data: { email_signature }
  })
}
